import { FC, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import {
  ArrowDownward,
  ArrowUpward,
  Block,
  Delete,
  PersonAdd,
  Send,
  Undo
} from "@mui/icons-material";
import { Signatory, SignOff } from "../../../api/Api";
import { AddVpDialog } from "./addVpDialog";
import { SIGN_OFF_CEO } from "./signOffCeo";
import { RejectSignOffDialog } from "./rejectSignOffDialog";
import { WithdrawSignOffDialog } from "./withdrawSignOffDialog";
import dayjs from "dayjs";

type Props = {
  signOff: SignOff | null | undefined;
  persistedSignatories: Signatory[];
  currentUserEmail: string;
  entityName?: string | null;
  /**
   * Seeds the signer list while the sign-off is still a draft and nothing has been persisted,
   * so the entity's VP is proposed without MA having to add them by hand. Once
   * signOff.signatories is non-empty this can never fire again.
   */
  defaultSignatories?: Signatory[];
  canManage: boolean;
  /** When set, "Send for sign off" is disabled and this is shown as the reason. */
  sendBlockedReason?: string | null;
  saving: boolean;
  onSaveDraft: (draft: SignOff) => void;
  onSendForSignOff: (draft: SignOff) => void;
  onSignCheckboxChange: (signatoryEmail: string, signed: boolean) => void;
  onSaveSignatures: () => void;
  onReject: (reason: string) => void;
  onResend: () => void;
  onWithdraw: () => void;
};

const reorder = (list: Signatory[]): Signatory[] => {
  const nonCeo = list.filter((s) => !s.isCeo);
  const ceo = list.filter((s) => s.isCeo);
  return [...nonCeo, ...ceo].map((s, idx) => ({ ...s, order: idx + 1 }));
};

// The CEO is hardcoded and must always be present as the final signatory. Append the
// configured CEO when one is missing (preserving any already-persisted CEO signature state).
const ensureCeo = (list: Signatory[]): Signatory[] =>
  list.some((s) => s.isCeo) ? reorder(list) : reorder([...list, SIGN_OFF_CEO]);

export const SignOffSignersPanel: FC<Props> = ({
  signOff,
  persistedSignatories,
  currentUserEmail,
  entityName,
  defaultSignatories,
  canManage,
  sendBlockedReason,
  saving,
  onSaveDraft,
  onSendForSignOff,
  onSignCheckboxChange,
  onSaveSignatures,
  onReject,
  onResend,
  onWithdraw
}) => {
  const status = signOff?.status ?? "draft";
  const isDraft = status === "draft" || !signOff;
  const isInProgress = status === "in_progress";
  const isCompleted = status === "completed";
  const isRejected = status === "rejected";

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);

  const initialSignatories = useMemo(
    () =>
      ensureCeo(
        signOff?.signatories?.length
          ? signOff.signatories
          : (defaultSignatories ?? [])
      ),
    [signOff, defaultSignatories]
  );

  const [draftSignatories, setDraftSignatories] =
    useState<Signatory[]>(initialSignatories);
  const [summary, setSummary] = useState<string>(signOff?.summary ?? "");
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const existingEmails = draftSignatories.map((s) => s.email);

  const handleAdd = (newSignatory: Signatory) => {
    setDraftSignatories((prev) => ensureCeo([...prev, newSignatory]));
  };

  const handleRemove = (email: string) => {
    // Keep the CEO regardless; only non-CEO signatories can be removed.
    setDraftSignatories((prev) =>
      ensureCeo(prev.filter((s) => s.isCeo || s.email !== email))
    );
  };

  const handleMove = (email: string, direction: -1 | 1) => {
    setDraftSignatories((prev) => {
      const nonCeo = prev.filter((s) => !s.isCeo);
      const idx = nonCeo.findIndex((s) => s.email === email);
      if (idx < 0) return prev;
      const target = idx + direction;
      if (target < 0 || target >= nonCeo.length) return prev;
      const next = nonCeo.slice();
      [next[idx], next[target]] = [next[target], next[idx]];
      const ceo = prev.filter((s) => s.isCeo);
      return reorder([...next, ...ceo]);
    });
  };

  // Spread the stored sign-off so fields this panel does not edit (comments, operational
  // readiness) survive a draft save or send.
  const buildDraft = (status: string): SignOff => ({
    ...signOff,
    status,
    summary,
    signatories: draftSignatories,
    createdBy: signOff?.createdBy ?? currentUserEmail,
    createdAt: signOff?.createdAt ?? new Date().toISOString()
  });

  // The CEO is always present; require at least one VP so a sign-off isn't sent with the CEO alone.
  const hasVp = draftSignatories.some((s) => !s.isCeo);
  const canSend = hasVp && !sendBlockedReason;

  if (isDraft) {
    return (
      <Box padding={1}>
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Configure sign off
            </Typography>
            {canManage ? (
              <>
                <TextField
                  label="Summary (optional)"
                  multiline
                  rows={3}
                  fullWidth
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  sx={{ marginBottom: 2 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 1
                  }}
                >
                  <Typography variant="subtitle2">
                    Signatories ({draftSignatories.length})
                  </Typography>
                  <Button
                    startIcon={<PersonAdd />}
                    size="small"
                    variant="outlined"
                    onClick={() => setAddDialogOpen(true)}
                  >
                    Add VP
                  </Button>
                </Box>
                <Divider />
                <List dense>
                  {draftSignatories.map((s) => {
                    const nonCeoList = draftSignatories.filter((x) => !x.isCeo);
                    const idx = nonCeoList.findIndex(
                      (x) => x.email === s.email
                    );
                    return (
                      <ListItem
                        key={s.email}
                        secondaryAction={
                          <Box>
                            {!s.isCeo && (
                              <>
                                <IconButton
                                  size="small"
                                  disabled={idx <= 0}
                                  onClick={() => handleMove(s.email, -1)}
                                >
                                  <ArrowUpward fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  disabled={
                                    idx < 0 || idx >= nonCeoList.length - 1
                                  }
                                  onClick={() => handleMove(s.email, 1)}
                                >
                                  <ArrowDownward fontSize="small" />
                                </IconButton>
                              </>
                            )}
                            {!s.isCeo && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemove(s.email)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1
                              }}
                            >
                              <span>
                                {s.order}. {s.name}
                              </span>
                              {s.isCeo && (
                                <Chip
                                  size="small"
                                  label="CEO"
                                  color="primary"
                                />
                              )}
                            </Box>
                          }
                          secondary={s.email}
                        />
                      </ListItem>
                    );
                  })}
                </List>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    marginTop: 2
                  }}
                >
                  <Button
                    variant="outlined"
                    disabled={saving}
                    loading={saving}
                    onClick={() => onSaveDraft(buildDraft("draft"))}
                  >
                    Save draft
                  </Button>
                  <Button
                    variant="contained"
                    endIcon={<Send />}
                    disabled={!canSend || saving}
                    loading={saving}
                    onClick={() => onSendForSignOff(buildDraft("in_progress"))}
                  >
                    Send for sign off
                  </Button>
                </Box>
                {!canSend && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", marginTop: 1 }}
                  >
                    {hasVp
                      ? sendBlockedReason
                      : "Add at least one VP signatory to send for sign off."}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Sign off has not been set up yet.
              </Typography>
            )}
          </CardContent>
          <AddVpDialog
            open={addDialogOpen}
            onClose={() => setAddDialogOpen(false)}
            onAdd={handleAdd}
            existingEmails={existingEmails}
          />
        </Card>
      </Box>
    );
  }

  const sorted = (signOff?.signatories ?? [])
    .slice()
    .sort((a, b) => a.order - b.order);
  const allNonCeoSigned = sorted.filter((s) => !s.isCeo).every((s) => s.signed);

  // A signatory VP can reject while the sign off is in progress, as an alternative to
  // signing. Once they have a persisted signature their row is locked and they can't reject.
  const currentUserSignatory = currentUserEmail
    ? sorted.find(
        (s) => s.email.toLowerCase() === currentUserEmail.toLowerCase()
      )
    : undefined;
  const currentUserPersistedSigned =
    !!currentUserSignatory &&
    persistedSignatories.some(
      (p) =>
        p.email.toLowerCase() === currentUserSignatory.email.toLowerCase() &&
        p.signed
    );
  const canReject =
    isInProgress && !!currentUserSignatory && !currentUserPersistedSigned;

  const title = isCompleted
    ? "Sign off completed"
    : isRejected
      ? "Sign off rejected"
      : "Sign off in progress";

  const currentUserCanSign =
    currentUserEmail &&
    sorted.find(
      (s) =>
        s.email.toLowerCase() === currentUserEmail.toLowerCase() && s.signed
    );
  return (
    <Box padding={1}>
      <Card elevation={3}>
        <CardContent>
          <Stack>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {!isCompleted &&
                !isRejected &&
                "Check the box next to your name and click the sign button"}
            </Typography>
          </Stack>
          <Divider />
          {isRejected && (
            <Alert severity="error" sx={{ marginTop: 2 }}>
              <AlertTitle>
                Rejected
                {signOff?.rejectedBy ? ` by ${signOff.rejectedBy}` : ""}
                {signOff?.rejectedAt
                  ? ` · ${dayjs(signOff.rejectedAt).format("LLL")}`
                  : ""}
              </AlertTitle>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {signOff?.rejectionReason || "No reason provided."}
              </Typography>
            </Alert>
          )}
          <List dense>
            {sorted.map((s) => {
              const isCurrentUser =
                currentUserEmail &&
                s.email.toLowerCase() === currentUserEmail.toLowerCase();
              // Non-CEO sign in parallel any time; CEO can only sign once every
              // other signatory is done. Once a signature is persisted on the
              // server it locks — local-only toggles can still be undone.
              const persisted = persistedSignatories.find(
                (p) => p.email.toLowerCase() === s.email.toLowerCase()
              );
              const persistedSigned = persisted?.signed === true;
              const gateOpen = s.isCeo ? allNonCeoSigned : true;
              const isAwaiting = !s.signed && gateOpen;
              const canTick =
                isInProgress && isCurrentUser && gateOpen && !persistedSigned;
              return (
                <ListItem key={s.email}>
                  <Checkbox
                    checked={s.signed}
                    disabled={!canTick}
                    onChange={(e) =>
                      onSignCheckboxChange(s.email, e.target.checked)
                    }
                  />
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 1
                        }}
                      >
                        <span>
                          {s.order}. {s.name}
                        </span>
                        {s.isCeo && (
                          <Chip size="small" label="CEO" color="primary" />
                        )}
                        {s.signed && (
                          <Chip
                            size="small"
                            label="Signed"
                            sx={{ backgroundColor: "#66D575", color: "white" }}
                          />
                        )}
                        {!s.signed && isAwaiting && (
                          <Chip size="small" label="Awaiting" color="warning" />
                        )}
                      </Box>
                    }
                    secondary={
                      s.signed && s.signedAt
                        ? `${s.email} · signed ${dayjs(s.signedAt).format("LLL")}`
                        : s.email
                    }
                  />
                </ListItem>
              );
            })}
          </List>
          {isInProgress && (
            <Box
              sx={{
                marginTop: 2,
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: 1
              }}
            >
              {canManage && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<Undo />}
                  disabled={saving}
                  onClick={() => setWithdrawDialogOpen(true)}
                >
                  Withdraw
                </Button>
              )}
              {canReject && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Block />}
                  disabled={saving}
                  onClick={() => setRejectDialogOpen(true)}
                >
                  Reject
                </Button>
              )}
              <Button
                variant="contained"
                disabled={saving || !currentUserCanSign}
                loading={saving}
                onClick={onSaveSignatures}
              >
                Sign
              </Button>
            </Box>
          )}
          {isRejected && canManage && (
            <Box
              sx={{
                marginTop: 2,
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                gap: 1
              }}
            >
              <Button
                variant="outlined"
                color="warning"
                startIcon={<Undo />}
                disabled={saving}
                onClick={() => setWithdrawDialogOpen(true)}
              >
                Withdraw
              </Button>
              <Button
                variant="contained"
                endIcon={<Send />}
                disabled={saving}
                loading={saving}
                onClick={onResend}
              >
                Send for sign off again
              </Button>
            </Box>
          )}
        </CardContent>
        <RejectSignOffDialog
          open={rejectDialogOpen}
          entityName={entityName}
          saving={saving}
          onClose={() => setRejectDialogOpen(false)}
          onConfirm={(reason) => {
            setRejectDialogOpen(false);
            onReject(reason);
          }}
        />
        <WithdrawSignOffDialog
          open={withdrawDialogOpen}
          entityName={entityName}
          saving={saving}
          onClose={() => setWithdrawDialogOpen(false)}
          onConfirm={() => {
            setWithdrawDialogOpen(false);
            onWithdraw();
          }}
        />
      </Card>
    </Box>
  );
};
